import Icon from '@components/Icon';
import Popover, { PopoverRef } from '@components/Popover';
import { PickerValidDate } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { ICONS_TYPES } from '../Icon';

import DateTextField from './components/DateTextField';
import { dateDefaultButtonsList } from './constants';
import { Sizes } from './enums';
import {
  AttentionIcon,
  ButtonsListContainer,
  DateButton,
  ErrorMessage,
  ErrorMessageContainer,
  Label,
  RequiredMark,
  Root,
  StyledDesktopDatePicker,
} from './styles';
import { Props } from './types';

const DatePicker = ({
  className,
  disabled = false,
  error,
  format = 'YYYY-MM-DD',
  fullWidth = false,
  label,
  loading,
  maxDate,
  minDate,
  name,
  onChange,
  popperZIndex = 1210,
  readOnly = false,
  required,
  showLabel,
  size = Sizes.medium,
  showExtraValues = true,
  value,
  view = ['year', 'month', 'day'],
}: Props) => {
  const valueRef = useRef<string | null | undefined>(value);
  const popoverRef = useRef<PopoverRef>(null);
  const disabledOrLoading = useMemo(
    () => disabled || loading,
    [disabled, loading],
  );
  const isClearable = useMemo(
    () => !readOnly && !disabled,
    [disabled, readOnly],
  );

  const handleChange = useCallback(
    (newDate: PickerValidDate | null) => {
      onChange?.(newDate ? moment(newDate as Moment).format(format) : null);
    },
    [format, onChange],
  );

  const handleChangeWithDefaultValue = useCallback(
    (pattern: string) => {
      const match = pattern.match(/^([+-])(\d+)m$/);
      if (!match) return;

      const [, sign, amountString] = match;
      const amount = Number(amountString);

      const base = valueRef.current
        ? moment(valueRef.current, format)
        : moment();
      const newDate =
        sign === '+'
          ? base.clone().add(amount, 'months')
          : base.clone().subtract(amount, 'months');

      popoverRef?.current?.close();
      onChange?.(newDate.format(format));
    },
    [format, onChange],
  );

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const defaultButtonsContent = useMemo(() => {
    return (
      <Popover
        ref={popoverRef}
        renderChildren={() => (
          <ButtonsListContainer>
            {dateDefaultButtonsList.map((el) => (
              <DateButton
                key={el.value}
                onClick={() => handleChangeWithDefaultValue(el.value)}>
                {el.label}
              </DateButton>
            ))}
          </ButtonsListContainer>
        )}>
        <Icon icon={ICONS_TYPES.ChevronDown} size={12} />
      </Popover>
    );
  }, [handleChangeWithDefaultValue]);
  const textField = useMemo(() => {
    return DateTextField(showExtraValues ? defaultButtonsContent : undefined);
  }, [defaultButtonsContent, showExtraValues]);

  return (
    <Root className={className}>
      {label && showLabel && (
        <Label htmlFor={name} error={!!error}>
          {required && !disabledOrLoading ? (
            <span>
              {label}
              <RequiredMark error={!!error}>*</RequiredMark>
            </span>
          ) : (
            label
          )}
        </Label>
      )}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <StyledDesktopDatePicker<string>
          disabled={disabledOrLoading}
          format={format}
          fullWidth={fullWidth}
          label={showLabel ? undefined : label}
          maxDate={maxDate ? moment(maxDate) : undefined}
          minDate={minDate ? moment(minDate) : undefined}
          name={name}
          onChange={handleChange}
          readOnly={readOnly}
          slots={{
            textField: textField,
          }}
          slotProps={{
            field: { clearable: isClearable },
            textField: {
              error: !!error,
              required: required,
            },
            popper: {
              sx: { zIndex: popperZIndex },
            },
          }}
          views={view}
          sizeType={size}
          value={value ? moment(value) : null}
        />
      </LocalizationProvider>
      {!!error && (
        <ErrorMessageContainer sizetype={size}>
          <AttentionIcon icon={ICONS_TYPES.Attention} size={20} />
          <ErrorMessage>{error}</ErrorMessage>
        </ErrorMessageContainer>
      )}
    </Root>
  );
};

export type { Props };
export { Sizes };

export default DatePicker;
